import { classicyLog } from "@/SystemFolder/SystemResources/Log/ClassicyLog";

const wallpaperModules = import.meta.glob(
	"../../../../assets/img/wallpapers/*.png",
	{ eager: true, import: "default" },
) as Record<string, string>;

const wp = (filename: string): string => {
	const key = `../../../../assets/img/wallpapers/${filename}`;
	return wallpaperModules[key] ?? "";
};

export const ClassicyDefaultWallpaper = wp("default.png");

/**
 * Turn a theme's `desktop.backgroundImage` into a URL the browser can actually
 * load.
 *
 * Bundled wallpapers must be referenced by name, never by path: `themes.json`
 * is imported as data, so Vite's asset pipeline never sees the strings inside
 * it and any path written there ships verbatim. Under `vite dev` such a path
 * happens to resolve (the file is served from the project root), but a built
 * library inlines wallpapers as `data:` URIs and emits no `/assets` directory
 * at all — so the literal path 404s in every consuming app.
 *
 * Values that are already loadable — `data:` URIs and remote URLs, including
 * a background the user set themselves — pass through untouched, which also
 * makes this idempotent and therefore safe to re-apply to persisted state.
 */
export const resolveWallpaper = (value: string): string => {
	if (!value) {
		return "";
	}
	if (/^(data:|blob:|https?:\/\/|\/\/)/.test(value)) {
		return value;
	}

	const filename = value.split("/").pop() ?? "";
	const resolved = wp(filename);
	if (!resolved) {
		classicyLog(
			"warn",
			"ClassicyWallpapers",
			"Unknown wallpaper; falling back to no background image.",
			{ value },
		);
	}
	return resolved;
};

export const ClassicyWallpapers = [
	{ label: "Azul Dark", value: wp("azul_dark.png") },
	{ label: "Azul Extra Light", value: wp("azul_extra_light.png") },
	{ label: "Azul Light", value: wp("azul_light.png") },
	{ label: "Bondi", value: wp("bondi.png") },
	{ label: "Bondi Dark", value: wp("bondi_dark.png") },
	{ label: "Bondi Extra Dark", value: wp("bondi_extra_dark.png") },
	{ label: "Bondi Light", value: wp("bondi_light.png") },
	{ label: "Bondi Medium", value: wp("bondi_medium.png") },
	{ label: "Bossanova Bondi", value: wp("bossanova_bondi.png") },
	{ label: "Bossanova Poppy", value: wp("bossanova_poppy.png") },
	{ label: "Bossanova Poppy 2", value: wp("bossanova_poppy_2.png") },
	{ label: "Bubbles Bondi", value: wp("bubbles_bondi.png") },
	{ label: "Bubbles Poppy", value: wp("bubbles_poppy.png") },
	{ label: "Candy Bar", value: wp("candy_bar.png") },
	{ label: "Candy Bar Azul", value: wp("candy_bar_azul.png") },
	{ label: "Candy Bar Pistachio", value: wp("candy_bar_pistachio.png") },
	{ label: "Candy Bar Sunny", value: wp("candy_bar_sunny.png") },
	{ label: "Default", value: wp("default.png") },
	{ label: "Diagonals Bondi", value: wp("diagonals_bondi.png") },
	{ label: "Diagonals Bondi Dark", value: wp("diagonals_bondi_dark.png") },
	{ label: "Diagonals Poppy", value: wp("diagonals_poppy.png") },
	{ label: "Flat Peanuts", value: wp("flat_peanuts.png") },
	{ label: "Flat Peanuts Poppy", value: wp("flat_peanuts_poppy.png") },
	{ label: "French Blue Dark", value: wp("french_blue_dark.png") },
	{ label: "French Blue Light", value: wp("french_blue_light.png") },
	{ label: "MacOS", value: wp("macos.png") },
	{ label: "Peanuts Azul", value: wp("peanuts_azul.png") },
	{ label: "Peanuts Pistachio", value: wp("peanuts_pistachio.png") },
	{ label: "Pistachio Dark", value: wp("pistachio_dark.png") },
	{ label: "Pistachio Light", value: wp("pistachio_light.png") },
	{ label: "Pistachio Medium", value: wp("pistachio_medium.png") },
	{ label: "Poppy", value: wp("poppy.png") },
	{ label: "Poppy Dark", value: wp("poppy_dark.png") },
	{ label: "Poppy Light", value: wp("poppy_light.png") },
	{ label: "Poppy Medium", value: wp("poppy_medium.png") },
	{ label: "Rio Azul", value: wp("rio_azul.png") },
	{ label: "Rio Pistachio", value: wp("rio_pistachio.png") },
	{ label: "Ripple Azul", value: wp("ripple_azul.png") },
	{ label: "Ripple Bondi", value: wp("ripple_bondi.png") },
	{ label: "Ripple Poppy", value: wp("ripple_poppy.png") },
	{ label: "Sunny", value: wp("sunny.png") },
	{ label: "Sunny Dark", value: wp("sunny_dark.png") },
	{ label: "Sunny Light", value: wp("sunny_light.png") },
	{ label: "Waves Azul", value: wp("waves_azul.png") },
	{ label: "Waves Bondi", value: wp("waves_bondi.png") },
	{ label: "Waves Sunny", value: wp("waves_sunny.png") },
];
